import { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaSpinner, FaVolumeUp } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import Constants from '../../../utilities/constants/Constants';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';

const DualMeaningActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState(
    initialContent
      ? (() => {
          try {
            return JSON.parse(initialContent);
          } catch {
            return Constants.DEFAULT_CONTENT.DUAL_MEANING;
          }
        })()
      : Constants.DEFAULT_CONTENT.DUAL_MEANING
  );
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);

  const buildStoragePath = ({ wordId, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'dual_meaning',
      fileName,
      itemId: wordId
    });
  };

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const addWord = () => {
    const newWords = [
      ...content.words,
      {
        languageWord: '',
        meaning1: '',
        meaning2: '',
        englishMeaning: '',
        languageWordAudio: null,
        meaning1Audio: null,
        meaning2Audio: null,
        englishMeaningAudio: null,
        confusingLetters1: [],
        confusingLetters2: [],
        image: null
      }
    ];
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const removeWord = async (index) => {
    if (content.words.length <= 1) return;

    const wordToDelete = content.words[index];
    const storage = getStorage();
    const fileTypes = [
      'languageWordAudio',
      'meaning1Audio',
      'meaning2Audio',
      'englishMeaningAudio',
      'image'
    ];

    for (const fileType of fileTypes) {
      const filePath = wordToDelete[fileType];
      if (filePath) {
        try {
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
        } catch (error) {
          // Continue even if delete fails
        }
      }
    }

    const newWords = content.words.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const updateWord = (index, field, value) => {
    const newWords = [...content.words];
    newWords[index] = { ...newWords[index], [field]: value };
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const updateConfusingLetters = (index, field, rawValue) => {
    const letters = rawValue
      .split(/[\s,]+/)
      .map(l => l.trim())
      .filter(l => l);
    updateWord(index, field, letters);
  };

  const handleFileUpload = async (index, fileType, file) => {
    if (!file || !chapterId || !grade) {
      return;
    }

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const word = content.words[index];
      const existingFilePath = word[fileType];

      if (existingFilePath) {
        try {
          const existingFileRef = ref(storage, existingFilePath);
          await deleteObject(existingFileRef);
        } catch (deleteError) {
          // Continue with upload even if deletion fails
        }
      }

      const storagePath = buildStoragePath({
        wordId: `word${index + 1}`,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const newWords = [...content.words];
      newWords[index] = {
        ...newWords[index],
        [fileType]: storagePath
      };
      setContent(prev => ({ ...prev, words: newWords }));
    } catch (error) {
      alert('Failed to upload audio. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = async (index, fileType) => {
    const word = content.words[index];
    const filePath = word[fileType];

    if (filePath) {
      try {
        const storage = getStorage();
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
      } catch (error) {
        // Continue with local removal
      }
    }

    const newWords = [...content.words];
    newWords[index] = { ...newWords[index], [fileType]: null };
    setContent(prev => ({ ...prev, words: newWords }));
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const downloadURL = await getDownloadURL(audioRef);
      const audio = new Audio(downloadURL);
      audio.play().catch(() => {});
    } catch (error) {
      alert(`Failed to play audio. Error: ${error.message}`);
    }
  };

  const generateAudioForAllWords = async () => {
    if (!content.words || content.words.length === 0) {
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const ttsRequests = [];

      content.words.forEach((word, index) => {
        if (word.languageWord && word.languageWord.trim() && !word.languageWordAudio) {
          ttsRequests.push({
            text: word.languageWord,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            field: 'languageWordAudio'
          });
        }
        if (word.meaning1 && word.meaning1.trim() && !word.meaning1Audio) {
          ttsRequests.push({
            text: word.meaning1,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            field: 'meaning1Audio'
          });
        }
        if (word.meaning2 && word.meaning2.trim() && !word.meaning2Audio) {
          ttsRequests.push({
            text: word.meaning2,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            field: 'meaning2Audio'
          });
        }
        if (word.englishMeaning && word.englishMeaning.trim() && !word.englishMeaningAudio) {
          ttsRequests.push({
            text: word.englishMeaning,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            wordIndex: index,
            field: 'englishMeaningAudio'
          });
        }
      });

      if (ttsRequests.length === 0) {
        setGeneratingAudio(false);
        return;
      }

      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );

      const updatedWords = [...content.words];

      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          const { wordIndex, field } = request;
          const word = updatedWords[wordIndex] || {};
          updatedWords[wordIndex] = {
            ...word,
            [field]: audioUrl
          };
        }
      });

      setContent(prev => ({ ...prev, words: updatedWords }));
    } catch (error) {
      alert('Failed to generate audio for words. Please try again.');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const validate = () => {
    const errors = [];

    if (!content.words || content.words.length === 0) {
      errors.push('At least one word is required');
    }

    content.words.forEach((word, index) => {
      if (!word.languageWord?.trim()) {
        errors.push(`Language word is required for item ${index + 1}`);
      }
      if (!word.meaning1?.trim()) {
        errors.push(`Meaning 1 is required for item ${index + 1}`);
      }
      if (!word.meaning2?.trim()) {
        errors.push(`Meaning 2 is required for item ${index + 1}`);
      }
      if (!word.englishMeaning?.trim()) {
        errors.push(`English meaning is required for item ${index + 1}`);
      }
    });

    return errors;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Dual Meaning Words</h3>
            <p className="text-sm text-gray-600 mt-1">
              {content.words.length} item{content.words.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              className={`flex items-center px-4 py-2 rounded-lg font-medium shadow-sm transition-colors ${
                generatingAudio
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
              onClick={generateAudioForAllWords}
              disabled={generatingAudio || !content.words || content.words.length === 0}
            >
              {generatingAudio ? (
                <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FaVolumeUp className="w-4 h-4 mr-2" />
              )}
              Auto-generate Audio
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addWord}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Word
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {content.words.map((word, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>

                <div className="flex-1 space-y-2">
                  {/* Line 1: Language word, Meaning 1, Meaning 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Language word *"
                      value={word.languageWord || ''}
                      onChange={(e) => updateWord(index, 'languageWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Meaning 1 (Language) *"
                      value={word.meaning1 || ''}
                      onChange={(e) => updateWord(index, 'meaning1', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Meaning 2 (Language) *"
                      value={word.meaning2 || ''}
                      onChange={(e) => updateWord(index, 'meaning2', e.target.value)}
                    />
                  </div>

                  {/* Line 2: English word, confusing letters for m1, confusing letters for m2 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="English word *"
                      value={word.englishMeaning || ''}
                      onChange={(e) => updateWord(index, 'englishMeaning', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Confusing letters for M1"
                      value={(word.confusingLetters1 || []).join(' ')}
                      onChange={(e) => updateConfusingLetters(index, 'confusingLetters1', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Confusing letters for M2"
                      value={(word.confusingLetters2 || []).join(' ')}
                      onChange={(e) => updateConfusingLetters(index, 'confusingLetters2', e.target.value)}
                    />
                  </div>
                </div>

                {/* Audio buttons next to each word (Language, M1, M2, English) and image upload */}
                <div className="flex flex-col gap-2 w-64">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-10 text-gray-600">Word</span>
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`dw-word-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'languageWordAudio', e.target.files[0])}
                    />
                    <label
                      htmlFor={`dw-word-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      🎵
                    </label>
                    {uploadingFiles[`${index}-languageWordAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.languageWordAudio && !uploadingFiles[`${index}-languageWordAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.languageWordAudio)}
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'languageWordAudio')}
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-10 text-gray-600">M1</span>
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`dw-m1-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'meaning1Audio', e.target.files[0])}
                    />
                    <label
                      htmlFor={`dw-m1-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      🎵
                    </label>
                    {uploadingFiles[`${index}-meaning1Audio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.meaning1Audio && !uploadingFiles[`${index}-meaning1Audio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.meaning1Audio)}
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'meaning1Audio')}
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-10 text-gray-600">M2</span>
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`dw-m2-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'meaning2Audio', e.target.files[0])}
                    />
                    <label
                      htmlFor={`dw-m2-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      🎵
                    </label>
                    {uploadingFiles[`${index}-meaning2Audio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.meaning2Audio && !uploadingFiles[`${index}-meaning2Audio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.meaning2Audio)}
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'meaning2Audio')}
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-10 text-gray-600">Eng</span>
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`dw-eng-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'englishMeaningAudio', e.target.files[0])}
                    />
                    <label
                      htmlFor={`dw-eng-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      🎵
                    </label>
                    {uploadingFiles[`${index}-englishMeaningAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {word.englishMeaningAudio && !uploadingFiles[`${index}-englishMeaningAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(word.englishMeaningAudio)}
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'englishMeaningAudio')}
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Image upload / preview */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-10 text-gray-600">Img</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`dw-image-${index}`}
                      onChange={(e) => handleFileUpload(index, 'image', e.target.files[0])}
                    />
                    <label
                      htmlFor={`dw-image-${index}`}
                      className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
                    >
                      🖼️
                    </label>
                    {uploadingFiles[`${index}-image`] && (
                      <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />
                    )}
                    {word.image && !uploadingFiles[`${index}-image`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          onClick={async () => {
                            try {
                              const storage = getStorage();
                              const imageRef = ref(storage, word.image);
                              const downloadURL = await getDownloadURL(imageRef);
                              window.open(downloadURL, '_blank');
                            } catch (error) {
                              alert(`Failed to preview image. Error: ${error.message}`);
                            }
                          }}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'image')}
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {content.words.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeWord(index)}
                    title="Remove Word"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
              <ul className="list-disc list-inside text-amber-700 text-sm space-y-1">
                {errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

export const validateDualMeaningContent = (content) => {
  const errors = [];

  if (!content.words || content.words.length === 0) {
    errors.push('At least one word is required');
  }

  content.words?.forEach((word, index) => {
    if (!word.languageWord?.trim()) {
      errors.push(`Language word is required for item ${index + 1}`);
    }
    if (!word.meaning1?.trim()) {
      errors.push(`Meaning 1 is required for item ${index + 1}`);
    }
    if (!word.meaning2?.trim()) {
      errors.push(`Meaning 2 is required for item ${index + 1}`);
    }
    if (!word.englishMeaning?.trim()) {
      errors.push(`English word is required for item ${index + 1}`);
    }
  });

  return errors;
};

export default DualMeaningActivity;
